import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { Shield, UserCheck, UserX, Mail, Send, X, Plus, RefreshCw, Clock } from 'lucide-react';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  restaurant_leader: 'Restaurantleiter',
  manager: 'Manager',
  service: 'Service',
  // legacy roles — shown read-only for existing users but not invitable
  hostess: 'Hostess (alt)',
  employee: 'Mitarbeiter (alt)',
  staff: 'Mitarbeiter (alt)',
};

const ROLE_COLORS = {
  super_admin: '#8B5CF6',
  admin: '#1C1C19',
  restaurant_leader: '#3B82F6',
  manager: '#22C55E',
  service: '#F97316',
  hostess: '#9CA3AF',
  employee: '#9CA3AF',
  staff: '#9CA3AF',
};

const INVITABLE_ROLES = ['admin', 'restaurant_leader', 'manager', 'service'];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

function isExpired(iso) {
  try { return new Date(iso).getTime() < Date.now(); } catch { return false; }
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [invForm, setInvForm] = useState({ email: '', role: 'employee' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([api.get('/users'), api.get('/invitations')]);
      setUsers(u.data);
      setInvites(i.data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}`, { role });
      toast.success(`Rolle: ${ROLE_LABELS[role]}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };
  const toggleActive = async (u) => {
    try {
      if (u.active) {
        await api.delete(`/users/${u.user_id}`);
        toast.success('Deaktiviert');
      } else {
        await api.put(`/users/${u.user_id}`, { active: true });
        toast.success('Aktiviert');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const sendInvitation = async () => {
    const email = invForm.email.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Bitte gültige E-Mail-Adresse');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/invitations', { email, role: invForm.role });
      toast.success(`Einladung an ${email} gesendet`);
      setShowInvite(false);
      setInvForm({ email: '', role: 'employee' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally { setSubmitting(false); }
  };

  const resendInvitation = async (inv) => {
    try {
      await api.post(`/invitations/${inv.id}/resend`);
      toast.success(`Erneut gesendet an ${inv.email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const revokeInvitation = async (inv) => {
    if (!window.confirm(`Einladung an ${inv.email} widerrufen?`)) return;
    try {
      await api.delete(`/invitations/${inv.id}`);
      toast.success('Widerrufen');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  if (forbidden) {
    return (
      <div className="p-8 max-w-2xl">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-8 text-center" data-testid="users-forbidden">
          <Shield className="w-10 h-10 mx-auto mb-3 text-[#EF4444]" />
          <h2 className="font-display text-xl mb-1">Keine Berechtigung</h2>
          <p className="text-sm text-[#5C5C59]">Diese Seite ist nur für Admins und Restaurantleiter zugänglich.</p>
        </div>
      </div>
    );
  }

  const pendingInvites = invites.filter(i => !i.accepted);

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-users-page">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Mitarbeiter</p>
          <h1 className="font-display text-3xl text-[#1C1C19]">Benutzerverwaltung</h1>
          <p className="text-sm text-[#5C5C59] mt-2 max-w-xl">
            Neue Mitarbeiter können sich erst nach einer Einladung anmelden.
            Senden Sie ihnen eine Einladung mit ihrer Google-E-Mail-Adresse.
          </p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          data-testid="invite-staff-btn"
          className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2.5 rounded text-sm flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Mitarbeiter einladen
        </button>
      </header>

      {/* Pending invitations */}
      {pendingInvites.length > 0 && (
        <section className="mb-6" data-testid="pending-invites-section">
          <h2 className="font-display text-lg mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#EAB308]" /> Offene Einladungen
            <span className="text-xs text-[#5C5C59] font-mono">({pendingInvites.length})</span>
          </h2>
          <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
            {pendingInvites.map(inv => {
              const expired = isExpired(inv.expires_at);
              return (
                <div key={inv.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`invite-row-${inv.id}`}>
                  <div className="w-10 h-10 rounded-full bg-[#EAB308]/15 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#EAB308]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-[#1C1C19]">{inv.email}</h3>
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: (ROLE_COLORS[inv.role] || '#9CA3AF') + '20', color: ROLE_COLORS[inv.role] || '#5C5C59' }}>
                        {ROLE_LABELS[inv.role]}
                      </span>
                      {expired && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Abgelaufen</span>}
                    </div>
                    <div className="text-xs text-[#5C5C59] flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {expired ? `Abgelaufen am ${formatDate(inv.expires_at)}` : `Gültig bis ${formatDate(inv.expires_at)}`}
                      {inv.invited_by_name && <> · von {inv.invited_by_name}</>}
                    </div>
                  </div>
                  <button
                    onClick={() => resendInvitation(inv)}
                    data-testid={`invite-resend-${inv.id}`}
                    className="px-3 py-1.5 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] flex items-center gap-1"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Erneut senden
                  </button>
                  <button
                    onClick={() => revokeInvitation(inv)}
                    data-testid={`invite-revoke-${inv.id}`}
                    className="px-3 py-1.5 border border-[#E5E5E0] rounded text-sm text-[#EF4444] hover:border-[#EF4444] flex items-center gap-1"
                  >
                    <X className="w-3.5 h-3.5" /> Widerrufen
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Users */}
      <section>
        <h2 className="font-display text-lg mb-2">Mitarbeiter</h2>
        <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
          {users.length === 0 && !loading && <div className="p-8 text-center text-[#5C5C59]">Noch keine Benutzer.</div>}
          {users.map(u => {
            const isMe = u.user_id === me?.user_id;
            const isSuper = u.role === 'super_admin';
            return (
              <div key={u.user_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`user-row-${u.user_id}`}>
                {u.picture ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full bg-[#F9F9F7] border border-[#E5E5E0] flex items-center justify-center font-display text-[#5C5C59]">{u.name?.[0] || '?'}</div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-[#1C1C19]">{u.name}</h3>
                    {isMe && <span className="text-xs px-1.5 py-0.5 bg-[#1C1C19] text-white rounded">Sie</span>}
                    {isSuper && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#8B5CF620', color: '#8B5CF6' }}>Super Admin</span>}
                    {!u.active && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Inaktiv</span>}
                  </div>
                  <div className="text-xs text-[#5C5C59]">{u.email}</div>
                </div>
                <select
                  value={u.role || 'service'}
                  onChange={e => changeRole(u, e.target.value)}
                  disabled={isMe || isSuper}
                  data-testid={`user-role-${u.user_id}`}
                  className="px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white disabled:opacity-60"
                  style={{ color: ROLE_COLORS[u.role] }}
                >
                  {isSuper && <option value="super_admin">Super Admin</option>}
                  {INVITABLE_ROLES.map(k => <option key={k} value={k}>{ROLE_LABELS[k]}</option>)}
                  {/* keep current legacy role visible so admin can keep or migrate it */}
                  {u.role && !INVITABLE_ROLES.includes(u.role) && u.role !== 'super_admin' && (
                    <option value={u.role}>{ROLE_LABELS[u.role] || u.role} – migrieren</option>
                  )}
                </select>
                <button
                  onClick={() => toggleActive(u)}
                  disabled={isMe || isSuper}
                  data-testid={`user-toggle-${u.user_id}`}
                  className="px-3 py-1.5 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] flex items-center gap-1 disabled:opacity-40"
                >
                  {u.active ? <><UserX className="w-3.5 h-3.5" /> Deaktivieren</> : <><UserCheck className="w-3.5 h-3.5" /> Aktivieren</>}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => !submitting && setShowInvite(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-display text-xl text-[#1C1C19]">Mitarbeiter einladen</h2>
                <p className="text-xs text-[#5C5C59] mt-1">Eine E-Mail mit Login-Anleitung wird verschickt.</p>
              </div>
              <button onClick={() => setShowInvite(false)} className="p-1 text-[#5C5C59]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Google-E-Mail-Adresse</label>
                <input
                  type="email"
                  value={invForm.email}
                  onChange={e => setInvForm({ ...invForm, email: e.target.value })}
                  placeholder="mitarbeiter@gmail.com"
                  data-testid="invite-email-input"
                  className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]"
                  autoFocus
                />
                <p className="text-[10px] text-[#5C5C59] mt-1">Die Person muss sich mit dieser Adresse via Google anmelden.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Rolle</label>
                <select
                  value={invForm.role}
                  onChange={e => setInvForm({ ...invForm, role: e.target.value })}
                  data-testid="invite-role-select"
                  className="w-full px-3 py-2 border border-[#E5E5E0] rounded bg-white"
                >
                  {INVITABLE_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowInvite(false)}
                disabled={submitting}
                data-testid="invite-cancel-btn"
                className="px-4 py-2 border border-[#E5E5E0] text-[#1C1C19] rounded text-sm"
              >
                Abbrechen
              </button>
              <button
                onClick={sendInvitation}
                disabled={submitting}
                data-testid="invite-send-btn"
                className="bg-[#1C1C19] hover:bg-[#3A3A35] disabled:opacity-50 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
              >
                <Send className="w-4 h-4" /> {submitting ? 'Sende...' : 'Einladung senden'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
